import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";


export default function ImportFromNJ({ onBack, onOpenEditPart }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const [selectedRowKey, setSelectedRowKey] = useState("");
  const [countryCode, setCountryCode] = useState("USA");
   const dialog = useAppDialog();

  useEffect(() => {
    mountedRef.current = true;

    async function initializePage() {
      try {
        setCountryCode("USA");
        await refreshData(false);

        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      } catch (error) {
        console.error("Failed to initialize ImportFromNJ:", error);
        await dialog.warning("Failed to load import list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }

    initializePage();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return { partNum: "", rev: 0, revMinor: 0 };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
    };
  }, [selectedRow]);

  const displayRows = useMemo(() => {
    const search = searchText.trim().toUpperCase();
    if (!search) return rows;

    return rows.filter((row) => {
      const partNumber = String(row.partNumber ?? "").toUpperCase();
      const printRevision = String(row.printRevision ?? "").toUpperCase();
      const mafiaRevision = String(row.mafiaRevision ?? "").toUpperCase();
      const createdBy = String(row.createdBy ?? "").toUpperCase();
      const timeStamp = formatDateTime(row.timeStamp).toUpperCase();

      return (
        partNumber.includes(search) ||
        printRevision.includes(search) ||
        mafiaRevision.includes(search) ||
        createdBy.includes(search) ||
        timeStamp.includes(search)
      );
    });
  }, [rows, searchText]);

  async function refreshData(showSpinner = true) {
    try {
      if (showSpinner) {
        setRefreshing(true);
      }

      const data = await getListOfNjParts();
      if (!mountedRef.current) return;

      const safeRows = data || [];
      setRows(safeRows);

      if (selectedRowKey) {
        const stillExists = safeRows.some((row) => getRowKey(row) === selectedRowKey);
        if (!stillExists) {
          setSelectedRowKey("");
        }
      }
    } catch (error) {
      console.error("Failed to refresh import data:", error);
      await dialog.warning("Failed to load import list.");
    } finally {
      if (mountedRef.current) {
        setRefreshing(false);
      }
    }
  }

  function selectRow(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
  }

  function openImportedEditPart(partData = selectedPart) {
    if (!partData.partNum) return;

    if (typeof onOpenEditPart === "function") {
      onOpenEditPart({
        partNum: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
        isImported: true,
        isNew: false,
      });
    } else {
      console.warn("Edit Part callback is not configured.");
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (displayRows.length > 0) {
      const firstMatch = displayRows[0];
      const rowKey = getRowKey(firstMatch);

      setSelectedRowKey(rowKey);

      const rowEl = rowRefs.current[rowKey];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  async function handleImportClick(partData = selectedPart) {
    if (!partData.partNum || importing) return;

    try {
      setImporting(true);

      const localTable = await getMafiaTable({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if ((localTable || []).length > 0) {
        if (countryCode === "USA") {
          await dialog.warning(
            "This part has already been imported from India and is already in the Florida MAFIA Central Database. Please use the 'Edit Part' button to edit already imported tables."
          );
        } else {
          await dialog.warning(
            "This part has already been imported from Florida and is already in the India MAFIA database. Please use the 'Edit Part' button to edit already imported tables."
          );
        }
        return;
      }

      const toCopy = await getMafiaTableSaturn({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if (!toCopy || toCopy.length === 0) {
        await dialog.error("Error in transferring table. Please try again or contact technical support.");
        return;
      }

      const copied = await copyToPluto(toCopy);
      if (!copied) {
        await dialog.error("Error in transferring table. Please try again or contact technical support.");
        return;
      }

      const initialized = await initializeIndiaTable({
        partNumber: partData.partNum,
        rev: partData.rev,
        revMinor: partData.revMinor,
      });

      if (!initialized) {
        await  dialog.error("Error in initializing India MAFIA table. Please contact technical support.");
        return;
      }

      openImportedEditPart(partData);
    } catch (error) {
      console.error("Import failed:", error);
      await dialog.error("Error in transferring table. Please try again or contact technical support.");
    } finally {
      if (mountedRef.current) {
        setImporting(false);
      }
    }
  }

  const title = countryCode === "USA" ? "Import from India" : "Import From NJ";
  const tabLabel = countryCode === "USA" ? "India Parts" : "NJ Parts";

  return (
    <div className="import-nj-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="import-nj-toolbar-main">
                <div className="import-nj-toolbar-main-left">
                  <Button color="soft-dark" className="import-nj-action-btn" onClick={onBack}>
                    <i className="ri-arrow-left-line import-nj-btn-icon me-1" />
                    Back
                  </Button>
                </div>

                <div className="import-nj-toolbar-main-right">
                  <InputGroup className="import-nj-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="border-start-0"
                      placeholder="Search part number"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="import-nj-toolbar-utility">
                <Button
                  color="primary"
                  className="import-nj-action-btn import-nj-utility-btn"
                  onClick={() => refreshData(true)}
                  disabled={refreshing || importing}
                >
                  <i className="ri-refresh-line import-nj-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh List"}
                </Button>

                <Button
                  color="success"
                  className="import-nj-action-btn import-nj-utility-btn"
                  onClick={() => handleImportClick()}
                  disabled={!selectedPart.partNum || importing}
                >
                  <i className="ri-download-cloud-2-line import-nj-btn-icon me-1" />
                  {importing ? "Importing..." : "Import Selected Part"}
                </Button>

                <Button
                  color="light"
                  className="import-nj-action-btn import-nj-utility-btn border"
                  onClick={() => openImportedEditPart()}
                  disabled={!selectedPart.partNum || importing}
                >
                  <i className="ri-edit-2-line import-nj-btn-icon me-1" />
                  Open Edit Part
                </Button>

                <Badge color="light" className="import-nj-queue-badge">
                  Queue Size: {displayRows.length}
                </Badge>

                <div className="import-nj-info-pill">
                  <span className="import-nj-info-label">Selected Part</span>
                  <span
                    className="import-nj-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="import-nj-info-pill">
                  <span className="import-nj-info-label">Revision</span>
                  <span className="import-nj-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="import-nj-refresh-note">
                  {importing ? "Import in progress" : `${title} • ${tabLabel}`}
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="import-nj-table-shell">
                <div className="import-nj-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 import-nj-table">
                    <thead>
                      <tr>
                        <th className="import-nj-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Print Revision</th>
                        <th>MAFIA Revision</th>
                        <th>Created By</th>
                        <th>Time Stamp</th>
                        <th>Verified</th>
                        <th>Released</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center py-5 text-muted">Loading...</td>
                        </tr>
                      ) : displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-5 text-muted">No parts found.</td>
                        </tr>
                      ) : (
                        displayRows.map((row) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={rowKey}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              tabIndex={0}
                              onClick={() => selectRow(row)}
                              onDoubleClick={() =>
                                handleImportClick({
                                  partNum: row.partNumber,
                                  rev: row.printRevision,
                                  revMinor: row.mafiaRevision,
                                })
                              }
                              className={`import-nj-data-row ${isSelected ? "selected" : ""}`}
                            >
                              <td className="import-nj-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => selectRow(row)}
                                  name="selected-import-row"
                                />
                              </td>
                              <td><div className="fw-semibold text-dark">{row.partNumber ?? ""}</div></td>
                              <td>{row.printRevision ?? ""}</td>
                              <td>{row.mafiaRevision ?? ""}</td>
                              <td>{row.createdBy ?? ""}</td>
                              <td>{formatDateTime(row.timeStamp)}</td>
                              <td><StatusBadge value={row.verified} /></td>
                              <td><StatusBadge value={row.released} /></td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .import-nj-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .import-nj-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .import-nj-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .import-nj-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .import-nj-search-group {
          width: 100%;
        }

        .import-nj-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .import-nj-utility-btn {
          min-width: 120px;
        }

        .import-nj-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .import-nj-queue-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #0f172a;
          background: #f8fafc;
        }

        .import-nj-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 300px;
        }

        .import-nj-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .import-nj-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .import-nj-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #64748b;
          font-weight: 500;
        }

        .import-nj-table-shell {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          overflow: hidden;
          background: #fff;
        }

        .import-nj-table-scroll {
          max-height: 540px;
          overflow: auto;
        }

        .import-nj-table {
          min-width: 1120px;
        }

        .import-nj-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0f172a;
          color: #f8fafc;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
          padding: 0.95rem 0.9rem;
          border-bottom: 0;
          white-space: nowrap;
        }

        .import-nj-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }

        .import-nj-col-select {
          width: 72px;
          text-align: center;
        }

        .import-nj-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .import-nj-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .import-nj-data-row:hover {
          background: #f5f9ff;
        }

        .import-nj-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .import-nj-status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.65rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
        }

        .import-nj-status.yes {
          background: rgba(25, 135, 84, 0.12);
          color: #198754;
        }

        .import-nj-status.no {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .import-nj-status.na {
          background: rgba(100, 116, 139, 0.12);
          color: #64748b;
        }

        @media (max-width: 1199.98px) {
          .import-nj-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .import-nj-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .import-nj-table-scroll {
            max-height: 460px;
          }

          .import-nj-info-pill {
            max-width: 100%;
          }

          .import-nj-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function StatusBadge({ value }) {
  if (value === true) {
    return (
      <span className="import-nj-status yes">
        <i className="ri-checkbox-circle-fill" /> Yes
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="import-nj-status no">
        <i className="ri-close-circle-fill" /> No
      </span>
    );
  }

  return <span className="import-nj-status na">—</span>;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function getListOfNjParts() {
  const response = await api.get("mafia/njparts");
  return (response.data || []).map((item) => ({
    partNumber: item.partNumber,
    printRevision: item.printRevision,
    mafiaRevision: item.mafiaRevision,
    createdBy: item.createdBy,
    timeStamp: item.timeStamp,
    verified: item.verified,
    released: item.released,
  }));
}

async function getMafiaTable(request) {
  const response = await api.post("mafia/mafiatable", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function getMafiaTableSaturn(request) {
  const response = await api.post("mafia/mafiatable-saturn", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function copyToPluto(rows) {
  const response = await api.post("mafia/copy-to-pluto", { rows });
  return response.data;
}

async function initializeIndiaTable(request) {
  const response = await api.post("mafia/initialize-india-table", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}